import { useRef, useCallback } from 'react'

export default function useSaveQueue(saveFunction, onSuccess, onError) {
  const saveQueue = useRef([])
  const isProcessing = useRef(false)

  // Process the queue
  const processQueue = useCallback(async () => {
    if (saveQueue.current.length === 0 || isProcessing.current) {
      return
    }

    isProcessing.current = true
    const operation = saveQueue.current.shift()

    try {
      const result = await saveFunction(operation)

      // Call success callback if provided
      if (onSuccess) {
        onSuccess(result, operation)
      }

      // Resolve the promise with the result
      operation.resolve(result)
    } catch (error) {
      // Call error callback if provided
      if (onError) {
        onError(error, operation)
      }

      // Reject the promise with the error
      operation.reject(error)
    } finally {
      isProcessing.current = false
      // Process next item in queue
      processQueue()
    }
  }, [saveFunction, onSuccess, onError])

  // Add an item to the queue
  const queueOperation = useCallback(
    (operation) => {
      return new Promise((resolve, reject) => {
        // Add this operation to the queue with resolve/reject functions
        saveQueue.current.push({
          ...operation,
          resolve,
          reject,
        })

        // Try to process the queue
        processQueue()
      })
    },
    [processQueue]
  )

  // Get current queue status
  const getQueueStatus = useCallback(() => {
    return {
      isProcessing: isProcessing.current,
      pendingCount: saveQueue.current.length,
    }
  }, [])

  // Clear the queue
  const clearQueue = useCallback(() => {
    saveQueue.current.forEach((op) => {
      op.reject(new Error('Queue cleared'))
    })
    saveQueue.current = []
  }, [])

  return {
    queueOperation,
    getQueueStatus,
    clearQueue,
  }
}
